import { isEmpty, isNotDefined } from '@sgme/fp';
import type { DocumentError } from 'apiConnector/document/document';
import { TinySpinner } from 'components/shared/TinySpinner/TinySpinner';
import { DocumentItem } from 'epic/common-form/Documentation';
import type { AnalyticCategoryActions, BlotterAnalyticCategory } from 'models/analytics';
import { DocumentType } from 'models/documents/DocumentType';
import { FC, useCallback, useMemo } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

import { BlotterPanelDocumentDownloadButton } from './BlotterPanelDocumentDownloadButton';
import { BlotterPanelDocumentDownloadButtonWithStatus } from './BlotterPanelDocumentDownloadButtonWithStatus';
import type { BlotterPanelDocumentListItemProps } from './BlotterPanelDocumentListItemLegacy';
import { BlotterPanelDocumentListItemLegacy } from './BlotterPanelDocumentListItemLegacy';

export type BlotterPanelDocumentProps = Omit<BlotterPanelDocumentListItemProps, 'children'> & {
  analyticsCategory?: BlotterAnalyticCategory;
  analyticsEvent?: AnalyticCategoryActions[BlotterAnalyticCategory];
  documents: DocumentItem[];
  isDownloading?: boolean;
  onDownload?: (doc: DocumentItem) => void;
  showDocumentStatus?: boolean;
  type: DocumentType;
  error?: DocumentError;
};

export const BlotterPanelDocument: FC<BlotterPanelDocumentProps> = ({
  analyticsCategory,
  analyticsEvent,
  documents,
  e2e,
  isDownloading,
  onDownload,
  showDocumentStatus = false,
  type,
  error,
  ...documentProps
}) => {
  const onClicked = useCallback(
    (doc: DocumentItem) => {
      onDownload?.(doc);

      if (analyticsCategory === undefined || analyticsEvent === undefined) {
        return;
      }
      logAnalytics.trackEvent(analyticsCategory, analyticsEvent, doc.language);
    },
    [analyticsCategory, analyticsEvent, onDownload],
  );

  const buttons = useMemo(
    () =>
      documents.map((doc, index) => {
        return isNotDefined(doc.language) || isEmpty(doc.language) ? (
          <BlotterPanelDocumentDownloadButton
            e2e={`${e2e}-download-button`}
            key={index}
            onClicked={() => onClicked(doc)}
            error={error}
            documentType={type}
          />
        ) : (
          <BlotterPanelDocumentDownloadButtonWithStatus
            document={doc}
            e2e={`${e2e}-download-button`}
            key={index}
            onClicked={() => onClicked(doc)}
            showStatus={showDocumentStatus}
            type={type}
            error={error}
          />
        );
      }),
    [showDocumentStatus, documents, e2e, type, error, onClicked],
  );

  return (
    <BlotterPanelDocumentListItemLegacy {...documentProps} e2e={e2e}>
      {isDownloading ? <TinySpinner className="mx-2 align-self-center" /> : buttons}
    </BlotterPanelDocumentListItemLegacy>
  );
};

BlotterPanelDocument.displayName = 'BlotterPanelDocument';
